import axios, { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router';

import { useSolutionContext } from '../../context/Solution/SolutionContext';
import Api from 'Api/Api';
import { url } from '@api-config';
import { dataSourceIngestionKeys } from './useDSIngestionStatus.query';
import { IngestionLogCountsQueryKey } from './useIngestionLogCounts.query';
import { ingestionLogsKeys } from './useIngestionLogSummary.query';
import { useAppDispatch } from '@redux/store';
import { openWarningModal } from '@redux/ErrorModal/error-modal-redux-slice';
import { SUBMIT_BUTTON_LABELS } from '@3.0/Buttons';
import { INGESTION_ERROR_TEXT } from '../constants/ingestion.constants';

export type TRunIngestionByTableProps = { dataSourceId: number; tableName: string };

export const useDSIngestionActions = () => {
  const { solution } = useSolutionContext();
  const { pathname } = useLocation();
  const query = useQueryClient();

  const dispatch = useAppDispatch();

  const id = solution?.id;

  const invalidateQueries = () => {
    if (pathname.includes('ingestion')) {
      query.invalidateQueries({
        queryKey: ingestionLogsKeys.all,
      });
    }
    query.invalidateQueries({ queryKey: IngestionLogCountsQueryKey });
    query.invalidateQueries({ queryKey: dataSourceIngestionKeys.invalidationKey });
  };

  const run = useMutation<void, Error, number>({
    mutationFn: (dsId) => {
      return axios.get(`${url.startIngestion}/${id}?includeDS=${dsId}`);
    },
    onSuccess: () => {
      invalidateQueries();
    },
    onError(error) {
      let errorDetails = isAxiosError(error) ? error.response?.data : error;
      dispatch(
        openWarningModal({
          errorDetails: errorDetails?.additionalDetails || {},
          text: {
            messages: {
              primary: errorDetails?.message || INGESTION_ERROR_TEXT.default,
            },
            buttons: { primary: SUBMIT_BUTTON_LABELS.ok },
          },
        })
      );
    },
  });

  const stop = useMutation<void, Error, string>({
    mutationFn: (ingId) => Api.get(`${url.stopIngestion}/${id}/${ingId}`),
    onSuccess: () => {
      invalidateQueries();
    },
  });

  const runByTable = useMutation<unknown, Error, TRunIngestionByTableProps>({
    mutationFn: ({ dataSourceId, tableName }: TRunIngestionByTableProps) => {
      return axios.post(`${url.startIngestionByTable}/${dataSourceId}`, {
        include: [tableName],
      });
    },
    onSuccess: () => {
      invalidateQueries();
    },
    onError(error) {
      let errorDetails = isAxiosError(error) ? error.response?.data : error;
      dispatch(
        openWarningModal({
          errorDetails: errorDetails?.additionalDetails || {},
          text: {
            messages: {
              primary: errorDetails?.message || INGESTION_ERROR_TEXT.default,
            },
            buttons: { primary: SUBMIT_BUTTON_LABELS.ok },
          },
        })
      );
    },
  });

  return {
    runByDS: run.mutate,
    stopByDS: stop.mutate,
    runByTable: runByTable.mutate,
  };
};
